import {
    Button,
    ErrorSummary,
    FormGroup,
    Link,
    MoreInfo,
    TextInput,
    Variant,
} from '@genomics-dev/denim-components';
import {StatusCodes} from 'http-status-codes';
import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';
import {
    useNavigate,
    useParams,
} from 'react-router-dom';
import {ContactSupport} from '../../../components';
import {useAmplitude} from '../../../context';
import {
    useEligibility,
    useLazyQuery,
    useTitle,
} from '../../../hooks';
import {useLinearFlowContext} from '../../../layouts';
import {Routes} from '../../../Routes';
import {
    devConsole,
    Endpoint,
} from '../../../util';

// This is used to try and tease apart whatever format they've chosen to enter their code with, we can try and
// reconstruct it into the format they were sent and the backend accepts...
const ACCESS_CODE_PATTERN = '\\s*[A-Za-z0-9]{4}\\s*-?\\s*[A-Za-z0-9]{4}\\s*';
const ACCESS_CODE_REGEX = /^(?<first>[A-Za-z0-9]{4})\s*-?\s*(?<second>[A-Za-z0-9]{4})$/im;

export function InviteCode() {
    const amplitude = useAmplitude();
    const {getEligibility, setEligibility} = useEligibility();
    const intl = useIntl();
    const [getInviteCodeAvailable] = useLazyQuery();
    const {nextStep} = useLinearFlowContext();
    const navigate = useNavigate();
    const {inviteCode} = useParams();
    const inviteCodeRef = useRef();
    const [error, setError] = useState(false);
    const [isPending, setIsPending] = useState(false);
    useTitle(intl.formatMessage({id: `signUp.inviteCode.documentTitle`}));

    useEffect(() => {
        // If the user has an invite code in the URL, we can pre-populate the input field with it...
        if (inviteCode && inviteCode.match(ACCESS_CODE_REGEX)) {
            setEligibility({inviteCode});
        }
    }, []);

    const onLoginClickHandler = useCallback((event) => {
        event.preventDefault();
        navigate(Routes.HOME);
    }, []);

    const onSubmitHandler = useCallback(
        async (event) => {
            event.preventDefault();

            const formValid = event.target.checkValidity();
            if (formValid) {
                // Get the value from the text input and strip any spaces...
                const accessCode = inviteCodeRef.current.value;
                const accessCodeWithNoSpaces = accessCode.replaceAll(/\s/gi, '');

                const result = accessCodeWithNoSpaces.match(ACCESS_CODE_REGEX);
                if (result) {
                    const {groups: {first, second} = {}} = result ?? {};
                    const correctlyFormattedAccessCode = `${first.toLocaleUpperCase()}-${second.toLocaleUpperCase()}`;

                    try {
                        setIsPending(true);

                        // Validate the invite code and store it...
                        const response = await getInviteCodeAvailable({
                            cacheKey: [Endpoint.INVITATIONS_AVAILABLE, correctlyFormattedAccessCode],
                            endpoint: `${Endpoint.INVITATIONS_AVAILABLE}/${correctlyFormattedAccessCode}`,
                            staleTime: 0,
                        });

                        setEligibility({
                            eligibleRegions: response.eligibleRegions,
                            inviteCode: correctlyFormattedAccessCode,
                        });
                        amplitude.logValidAccessCodeEntered();

                        // Continue to the next step...
                        nextStep();
                    }
                    catch (err) {
                        setError(err);
                        if (err.cause?.status === StatusCodes.GONE) {
                            amplitude.logInactiveAccessCodeEntered(accessCode);
                        }
                        else {
                            amplitude.logInvalidAccessCodeEntered(accessCode);
                        }

                        devConsole(err);
                    }
                    finally {
                        setIsPending(false);
                    }
                }
                else {
                    amplitude.logInvalidAccessCodeEntered(accessCode);
                }
            }
        },
        [amplitude, nextStep, getInviteCodeAvailable, setEligibility],
    );

    return (
        <form
            noValidate={true}
            onSubmit={onSubmitHandler}
        >
            <h2 data-test-id={'sign-up-invite-code-title'}>
                <FormattedMessage id={'signUp.inviteCode.title'} />
            </h2>
            <p className={'description'}>
                <FormattedMessage
                    id={inviteCode
                        ? 'signUp.inviteCodeLink.description'
                        : 'signUp.inviteCode.description'}
                />
            </p>
            {error && (
                <ErrorSummary
                    dataTestId={'sign-up-error-summary'}
                    errors={[
                        [StatusCodes.NOT_FOUND, StatusCodes.GONE].includes(error.cause.status)
                            ? intl.formatMessage(
                                {
                                    id: inviteCode
                                        ? 'signUp.inviteCodeLink.error.description'
                                        : 'signUp.inviteCode.error.description',
                                },
                                {cs: (chunk) => <ContactSupport text={chunk} />},
                            )
                            : intl.formatMessage(
                                {id: 'fetchError.information'},
                                {cs: (chunk) => <ContactSupport text={chunk} />},
                            ),
                    ]}
                    title={[StatusCodes.NOT_FOUND, StatusCodes.GONE].includes(error.cause.status)
                        ? intl.formatMessage(
                            {id: 'signUp.inviteCode.error.title'},
                            {code: () => inviteCode ? inviteCode.toUpperCase() : ''},
                        )
                        : intl.formatMessage({id: 'fetchError.title'})}
                />
            )}
            <FormGroup
                dataTestId={'sign-up-invite-code-form-group'}
                label={intl.formatMessage({id: 'signUp.inviteCode.label'})}
                labelFor={'inviteCode'}
            >
                <TextInput
                    dataTestId={'sign-up-invite-code-input'}
                    errorMessages={{
                        patternMismatch: intl.formatMessage({id: 'signUp.inviteCode.patternMismatch'}),
                        valueMissing: intl.formatMessage({id: 'signUp.inviteCode.valueMissing'}),
                    }}
                    id={'invite-code'}
                    ignoreOnChangeInvalidity={true}
                    isLoading={isPending}
                    name={'inviteCode'}
                    pattern={ACCESS_CODE_PATTERN}
                    ref={inviteCodeRef}
                    required={true}
                    type={'text'}
                    value={getEligibility()?.inviteCode ?? inviteCode}
                />
            </FormGroup>
            <Button
                className={'no-margin icon arrow--right after'}
                dataTestId={'sign-up-page-continue'}
                submit={true}
                title={intl.formatMessage({id: 'common.button.continue.title'})}
                variant={Variant.PRIMARY}
            >
                <FormattedMessage id={'common.button.continue'} />
            </Button>
            <p className={'variant-dark'}>
                <FormattedMessage
                    id={'signUp.inviteCode.existingAccount'}
                    values={{
                        login: (chunk) => (
                            <Link
                                className={'alternate'}
                                onClick={onLoginClickHandler}
                                url={'#'}
                            >
                                {chunk}
                            </Link>
                        ),
                    }}
                />
            </p>
            <MoreInfo
                content={
                    <p className={'variant-dark'}>
                        <FormattedMessage
                            id={'signUp.inviteCode.requestCode.content'}
                            values={{
                                cs: (chunk) => (
                                    <ContactSupport
                                        className={'alternate'}
                                        text={chunk}
                                    />
                                ),
                            }}
                        />
                    </p>
                }
                title={intl.formatMessage({id: 'signUp.inviteCode.requestCode.title'})}
            />
        </form>
    );
}
